import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Real-time employee status board. Polls /api/timeclock/status-board.
 *
 * Props:
 *   variant: 'kiosk' (dark theme for /clock) | 'admin' (light theme for /checador).
 *   pollMs: poll interval (default 8000).
 *   onUpdate: callback fired after each refresh (used by kiosk to refresh after a punch).
 *   refreshKey: incrementing number to force an immediate refresh.
 */
export default function StatusBoard({ variant = 'admin', pollMs = 8000, refreshKey = 0 }) {
  const [data, setData] = useState({ rows: [], updated_at: null });
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/timeclock/status-board`);
      setData(r.data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, pollMs);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [pollMs]);

  // Force-refresh when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) load();
    // eslint-disable-next-line
  }, [refreshKey]);

  const ins = data.rows.filter(r => r.status === 'in');
  const outs = data.rows.filter(r => r.status === 'out');

  if (variant === 'kiosk') {
    return (
      <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-4 max-h-[80vh] overflow-y-auto" data-testid="status-board-kiosk">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-widest text-white/60 font-semibold">Personal en turno</p>
          <span className="text-[11px] text-white/40">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1"></span>
            En vivo
          </span>
        </div>
        {data.rows.length === 0 ? (
          <p className="text-xs text-white/40">Sin empleados</p>
        ) : (
          <div className="space-y-1">
            {[...ins, ...outs].map(r => (
              <div key={r.employee_id} className="flex items-center justify-between text-sm py-1.5 px-2 rounded-md hover:bg-white/5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${r.status === 'in' ? 'bg-emerald-400' : 'bg-rose-500/80'}`} />
                  <span className={`truncate ${r.status === 'in' ? 'text-white' : 'text-white/50'}`}>{r.name}</span>
                </div>
                {r.status === 'in' && (
                  <span className="text-[11px] font-mono text-emerald-300/80 ml-2">desde {fmtTime(r.clock_in)}</span>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-[11px] text-white/40">
          <span><span className="text-emerald-300 font-semibold">{ins.length}</span> en turno</span>
          <span>{data.rows.length} total</span>
        </div>
      </div>
    );
  }

  // ADMIN VARIANT
  return (
    <div className="bg-white rounded-lg border border-slate-200" data-testid="status-board-admin">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Tablero en vivo</h3>
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Actualizando cada {Math.round(pollMs / 1000)}s
          </span>
        </div>
        <div className="text-xs text-slate-500">
          <span className="text-emerald-600 font-semibold">{ins.length}</span> en turno · {data.rows.length} total
        </div>
      </div>
      {data.rows.length === 0 ? (
        <p className="text-center text-slate-400 text-xs py-8">Sin empleados activos</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {[...ins, ...outs].map(r => (
            <div key={r.employee_id} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50">
              <div className="flex items-center gap-3 min-w-0">
                {r.status === 'in' ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    IN
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                    OUT
                  </span>
                )}
                <span className={`text-sm font-medium ${r.status === 'in' ? 'text-slate-900' : 'text-slate-500'}`}>{r.name}</span>
              </div>
              {r.status === 'in' && r.clock_in && (
                <span className="text-xs text-slate-500 font-mono">desde {fmtTime(r.clock_in)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
